// This component will be depricated in future, use SpeechToTextVisualiser instead

import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

/**
 * updateAudioRecorderBlob will be used to save recording blob in redux as draft
 */
import {
  updateAudioRecorderBlob,
  setAudioRecorderActive,
  getSpeechToTextTranscript,
} from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { Alert, IconButton, Tooltip, Button } from "@toddle-edu/ds-web";

import I18nHOC from "UIComponents/I18nHOC";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import CompactAudioWaveformPlayer from "UIComponents/CompactAudioWaveformPlayer";
import withRecorder from "../WithRecorder";
import CircleLoading from "UIComponents/CircleLoading";
import GenerateTranscriptsButton from "AppComponents/AIComponents/GenerateTranscriptsButton";

import classes from "./AudioRecorderVisualiser.scss";
import {
  CloseOutlined,
  DeleteOutlined,
  MicrophoneOutlined,
  PauseFilled,
  TickCircleFilled,
  VoiceWaveOutlined,
} from "@toddle-edu/ds-icons";
import { onEnterOrSpaceKeyDown, prepareAttachmentFromBlob } from "Utils";

const AudioRecorderVisualiser = forwardRef(
  (
    {
      t,
      alertContainerStyle,
      cancelRecording,
      draftRecording,
      draftRecordingBlob,
      formattedLimit,
      isPaused,
      isPermissionGranted,
      playerState,
      stopRecording,
      startRecording,
      togglePause,
      timer,
      handleVisualiserRefUpdate,
      pauseStateBlob,
      activeAudioRecorder,
      setAudioRecorderActive,
      isSpeechToTextVisualiser = false,
      audioRecorderVisualizerStyle,
      source = "DEFAULT",
      speechToTextParams = {},
      getSpeechToTextTranscript,
      isLoading,
      updateIsLoading,
      handleAudioFile,
      timeStamp,
      minRecordingDurationMs = 0,
    },
    forwardedRef
  ) => {
    const [alertVisible, setAlertVisible] = useState(isPermissionGranted);

    useImperativeHandle(forwardedRef, () => ({
      cancelRecording: () => {
        cancelRecording();
      },
    }));

    const onVisualiserContainerRefUpdate = useCallback(node => {
      if (node !== null) {
        handleVisualiserRefUpdate(node);
      }
    }, []);

    const onAlertClose = () => {
      cancelRecording();
      setAlertVisible(false);
    };

    const getAttachment = () => {
      if (pauseStateBlob) {
        return prepareAttachmentFromBlob(pauseStateBlob);
      } else if (draftRecordingBlob) {
        return prepareAttachmentFromBlob(draftRecordingBlob);
      }
      return null;
    };

    const renderRecorder = () => {
      const canRenderRecorder = playerState !== "cancelled" && !draftRecording;

      /**
       * add usage of this method
       */
      const onRecordClick = () => {
        if (!isPermissionGranted) setAlertVisible(true);
        startRecording(); // starts recording, if permission is not given then asks for it.
      };

      const dsVersion = isSpeechToTextVisualiser ? { dsVersion: "2.0" } : {};

      const renderAudioRecorder = () => {
        switch (source) {
          case "DEFAULT":
            return (
              <div
                className={classes.audioRecorderVisualizer}
                style={audioRecorderVisualizerStyle}
              >
                <div className={classes.visualDetails}>
                  <div
                    className={classes.visualiserContainer}
                    style={{ display: isPaused && "none" }}
                    ref={onVisualiserContainerRefUpdate}
                  ></div>

                  <div
                    className={classes.recordingTimer}
                    style={{ display: isPaused && "none" }}
                  >
                    <div className={classes.recordingIndicator} />
                    {timer}/{formattedLimit}
                  </div>
                  {isPaused && (
                    <MediaPlaceholder
                      attachments={[getAttachment()]}
                      showAudioDownloadButton={false}
                    />
                  )}
                </div>
                <div className={classes.actions}>
                  <div className={classes.buttonsContainer}>
                    <div>
                      <Tooltip
                        tooltip={t("common:complete_recording")}
                        placement="top"
                        showArrow={true}
                      >
                        <IconButton
                          {...dsVersion}
                          variant="progressive"
                          size="small"
                          icon={<TickCircleFilled />}
                          onClick={stopRecording}
                          onKeyDown={e =>
                            onEnterOrSpaceKeyDown(e, stopRecording)
                          }
                        />
                      </Tooltip>
                    </div>
                    {!isSpeechToTextVisualiser ? (
                      <Tooltip
                        placement="top"
                        tooltip={
                          isPaused
                            ? t("common:resume_recording")
                            : t("common:pause_recording")
                        }
                        showArrow={true}
                      >
                        <IconButton
                          _legacy_variant="plain"
                          variant="neutral"
                          type="plain"
                          size="small"
                          icon={
                            isPaused ? (
                              <MicrophoneOutlined variant="subtle" />
                            ) : (
                              <PauseFilled />
                            )
                          }
                          onClick={() => togglePause(!isPaused)}
                          onKeyDown={e =>
                            onEnterOrSpaceKeyDown(e, () =>
                              togglePause(!isPaused)
                            )
                          }
                          disabled={isLoading}
                        />
                      </Tooltip>
                    ) : null}
                    {isSpeechToTextVisualiser ? (
                      <IconButton
                        {...dsVersion}
                        icon={<CloseOutlined />}
                        onClick={cancelRecording}
                        onKeyDown={e =>
                          onEnterOrSpaceKeyDown(e, cancelRecording)
                        }
                        variant="neutral"
                        type="outlined"
                        size="small"
                      />
                    ) : (
                      <Tooltip
                        placement="top"
                        tooltip={t("common:cancel_recording")}
                        showArrow={true}
                      >
                        <IconButton
                          icon={<DeleteOutlined />}
                          onClick={cancelRecording}
                          onKeyDown={e =>
                            onEnterOrSpaceKeyDown(e, cancelRecording)
                          }
                          _legacy_variant="plain"
                          variant="neutral"
                          type="plain"
                          size="small"
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            );
          case "AI_TUTOR": {
            const createButtonElement = attachment => {
              if (!attachment) return null;

              const handleGenerateTranscript = async () => {
                try {
                  const transcriptResponse = await getSpeechToTextTranscript({
                    blobAttachment: { file: attachment.file },
                    ...speechToTextParams,
                  });

                  return transcriptResponse?.transcript || "";
                } catch {
                  return "";
                }
              };

              return (
                <GenerateTranscriptsButton
                  id={attachment.url}
                  onGenerateTranscript={handleGenerateTranscript}
                  isStudent={true}
                  buttonSize={"x-small"}
                />
              );
            };

            const handleCompleteRecording = () => {
              if (
                minRecordingDurationMs > 0 &&
                timeStamp < minRecordingDurationMs
              ) {
                cancelRecording();
                return;
              }

              togglePause(true);
              updateIsLoading(true);
            };

            const attachment = getAttachment();

            return (
              <div
                className={classes.audioRecorderVisualizer}
                style={audioRecorderVisualizerStyle}
              >
                <div className={classes.tutorVisualDetails}>
                  <div
                    className={classes.visualiserContainer}
                    style={{ display: isPaused && !isLoading && "none" }}
                    ref={onVisualiserContainerRefUpdate}
                  ></div>

                  <div
                    className={classes.tutorRecordingTimer}
                    style={{ display: isPaused && !isLoading && "none" }}
                  >
                    <div className={classes.recordingIndicator} />
                    {timer}/{formattedLimit}
                  </div>
                  {isPaused && !isLoading && attachment && (
                    <CompactAudioWaveformPlayer
                      attachment={attachment}
                      buttonElement={createButtonElement(attachment)}
                      showDownload={false}
                    />
                  )}
                </div>
                <div className={classes.tutorRecorder}>
                  <Tooltip
                    placement="top"
                    tooltip={t("common:cancel_recording")}
                    showArrow={true}
                  >
                    <IconButton
                      icon={<DeleteOutlined variant={"critical"} />}
                      onClick={cancelRecording}
                      onKeyDown={e => onEnterOrSpaceKeyDown(e, cancelRecording)}
                      _legacy_variant="plain"
                      variant="neutral"
                      type="plain"
                      size="small"
                      disabled={isLoading}
                    />
                  </Tooltip>
                  <Tooltip
                    placement="top"
                    tooltip={
                      isPaused
                        ? t("common:resume_recording")
                        : t("common:pause_recording")
                    }
                    showArrow={true}
                  >
                    {isPaused && !isLoading ? (
                      <Button
                        type={"outlined"}
                        size={"small"}
                        dsVersion={"2.0"}
                        variant={"neutral"}
                        icon={<VoiceWaveOutlined />}
                        testId="chatFooter-audioButton"
                        onClick={() => togglePause(!isPaused)}
                        onKeyDown={e =>
                          onEnterOrSpaceKeyDown(e, () => togglePause(!isPaused))
                        }
                        disabled={isLoading}
                      >
                        {t("ai:resume_talking")}
                      </Button>
                    ) : (
                      <IconButton
                        _legacy_variant="plain"
                        variant="neutral"
                        type="plain"
                        size="small"
                        icon={<PauseFilled />}
                        onClick={() => togglePause(!isPaused)}
                        onKeyDown={e =>
                          onEnterOrSpaceKeyDown(e, () => togglePause(!isPaused))
                        }
                        disabled={isLoading}
                      />
                    )}
                  </Tooltip>

                  {isLoading ? (
                    <CircleLoading size="small" />
                  ) : (
                    <Tooltip
                      tooltip={t("common:complete_recording")}
                      placement="top"
                      showArrow={true}
                    >
                      <IconButton
                        {...dsVersion}
                        variant="progressive"
                        size="small"
                        icon={<TickCircleFilled />}
                        onClick={handleCompleteRecording}
                        onKeyDown={e =>
                          onEnterOrSpaceKeyDown(e, handleCompleteRecording)
                        }
                        disabled={isLoading}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          }
        }
      };

      return canRenderRecorder &&
        isPermissionGranted &&
        (playerState === "record" || playerState === "recording" || isLoading)
        ? renderAudioRecorder()
        : null;
    };

    useEffect(() => {
      setAudioRecorderActive({
        ...activeAudioRecorder,
        recorderAlreadyActive: true,
        closeActiveAudioRecorder: cancelRecording,
      });
      return () => {
        setAudioRecorderActive(null);
      };
    }, []);

    useEffect(() => {
      if (source !== "AI_TUTOR") return;

      if (isPaused && pauseStateBlob && isLoading) {
        const attachment = getAttachment();

        if (!_.isEmpty(attachment)) {
          handleAudioFile?.(attachment);
        }
      }
    }, [isPaused, pauseStateBlob, isLoading]);

    return (
      <>
        {!isPermissionGranted ? (
          <div className={classes.alertContainer} style={alertContainerStyle}>
            {alertVisible ? (
              <Alert
                title={t("common:microphone_permission_message")}
                type={"warning"}
                isClosable={true}
                onClose={onAlertClose}
              />
            ) : null}
          </div>
        ) : null}
        {renderRecorder()}
      </>
    );
  }
);

AudioRecorderVisualiser.displayName = "AudioRecorderVisualiser";

const mapActionCreators = {
  setToastMsg,
  updateAudioRecorderBlob,
  setAudioRecorderActive,
  getSpeechToTextTranscript,
};
const mapStateToProps = state => {
  return {
    draftRecording: _.get(state, "app_services.draftRecording", null),
    activeAudioRecorder: _.get(
      state,
      "app_services.activeAudioRecorder",
      false
    ),
  };
};

const propTypes = {};

const defaultProps = {
  /**
   * ! If setting autoStart value as false then it should be noted that the pause button is rendered rather than start recording
   * ! getting error with the isRecording props
   */
  autoStartRecording: true,
  saveDraftRecording: true,
  isSpeechToTextVisualiser: false,
};

AudioRecorderVisualiser.propTypes = propTypes;
AudioRecorderVisualiser.defaultProps = defaultProps;

const ComposedAudioRecorderVisualiser = compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators, null, { forwardRef: true }),
  withRecorder
)(AudioRecorderVisualiser);

ComposedAudioRecorderVisualiser.propTypes = propTypes;
ComposedAudioRecorderVisualiser.defaultProps = defaultProps;

export default ComposedAudioRecorderVisualiser;
